import {
  Box,
  Button,
  Card,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchTwoToneIcon from '@mui/icons-material/SearchTwoTone';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import axios from 'axios';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';

import { TableLoader, TableNotFound } from 'src/Utils/TableLoader';

import AnnoucementTableRow from './AnnoucementTableRow';
import AnnoucementTabs from './AnnouncementTab';
import { IAnnoucement } from 'src/Types/module/announcement/announcementType';
import CreateAnnoucement from './CreateAnnoucement';
import ElectionBoardNotice from './ElectionBoardNotice';

const AnnoucementTable = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  const [annoucements, setAnnoucements] = useState<IAnnoucement[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<
    'ALL' | 'NEWS' | 'NOTICE' | 'MEMBER_NOTICE'
  >('ALL');
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  // Modal
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [searchParams, setSearchParams] = useSearchParams({
    tab: 'announcement',
  });
  const tabQuery = searchParams.get('tab') || 'announcement';
  const isElectionBoard = tabQuery.toLowerCase() === 'election-board';
  const tabValue = isElectionBoard ? 1 : 0;

  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  useEffect(() => {
    if (isElectionBoard) return;
    setIsLoading(true);
    (async function () {
      try {
        const uri = `${url}/api/v1/admin/announcement`;
        const params: any = {
          search: debouncedSearch || undefined,
        };

        if (
          typeFilter === 'NEWS' ||
          typeFilter === 'NOTICE' ||
          typeFilter === 'MEMBER_NOTICE'
        ) {
          params.type = typeFilter;
        }

        const { data } = await axios.get(uri, {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (data.success) {
          setAnnoucements(data.data);
          setIsLoading(false);
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        setIsLoading(false);
      }
    })();
  }, [isElectionBoard, refreshTrigger, debouncedSearch, typeFilter]);

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setSearchParams({
      tab: newValue === 1 ? 'election-board' : 'announcement',
    });
  };

  return (
    <>
      <Box
        sx={{
          mx: 3,
          mb: 1.5,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <AnnoucementTabs value={tabValue} handleChange={handleChange} />

        {!isElectionBoard && (
          <Box display='flex' alignItems='center' gap={1.5} flexWrap='wrap'>
            <FormControl size='small'>
              <Select
                value={typeFilter}
                onChange={(e) =>
                  setTypeFilter(e.target.value as 'ALL' | 'NEWS' | 'NOTICE')
                }
                startAdornment={
                  <InputAdornment position='start'>
                    <FilterListIcon
                      fontSize='small'
                      sx={{ color: 'text.secondary' }}
                    />
                  </InputAdornment>
                }
                sx={{
                  minWidth: 150,
                  backgroundColor: 'background.paper',
                  borderRadius: '10px',
                  fontSize: '14px',
                  fontWeight: 500,
                }}
              >
                <MenuItem value='ALL'>All Types</MenuItem>
                <MenuItem value='NEWS'>NEWS</MenuItem>
                <MenuItem value='NOTICE'>NOTICE</MenuItem>
                <MenuItem value='MEMBER_NOTICE'>MEMBER NOTICE</MenuItem>
              </Select>
            </FormControl>

            <TextField
              size='small'
              placeholder='Search...'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <SearchTwoToneIcon sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: { xs: '100%', sm: 240 },
                backgroundColor: 'background.paper',
                borderRadius: 1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                },
              }}
            />
          </Box>
        )}
      </Box>

      {isElectionBoard ? (
        <ElectionBoardNotice />
      ) : (
        <Card sx={{ mx: 3 }}>
          <CardHeader
            action={
              <Box>
                <Button
                  onClick={() => handleOpen()}
                  variant='contained'
                  sx={{ ml: 2 }}
                >
                  Create Announcement
                </Button>
              </Box>
            }
            title='Announcement List'
          />
          <Divider />
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>SL</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>

              {isLoading ? (
                <TableLoader />
              ) : annoucements?.length ? (
                <TableBody>
                  {annoucements.map((announcement, i) => {
                    return (
                      <AnnoucementTableRow
                        key={announcement.id}
                        index={i}
                        announcement={announcement}
                        annoucements={annoucements}
                        setAnnoucements={setAnnoucements}
                        setRefreshTrigger={setRefreshTrigger}
                      />
                    );
                  })}
                </TableBody>
              ) : (
                <TableNotFound />
              )}
            </Table>
          </TableContainer>

          {/* Create Modal */}
          <Dialog
            fullScreen={fullScreen}
            open={open}
            maxWidth='md'
            onClose={handleClose}
            aria-labelledby='responsive-dialog-title'
          >
            <DialogContent>
              <Box sx={{ mb: 2 }}>
                <Typography variant='h4' sx={{ mb: 1 }}>
                  Create Announcement
                </Typography>
                <Divider />
              </Box>
              <CreateAnnoucement
                handleClose={handleClose}
                setAnnoucements={setAnnoucements}
                setRefreshTrigger={setRefreshTrigger}
              />
            </DialogContent>
            <DialogActions>
              <Button autoFocus onClick={handleClose}>
                Close
              </Button>
            </DialogActions>
          </Dialog>
        </Card>
      )}
    </>
  );
};

export default AnnoucementTable;
